import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Phone, X, Star, Trash2, MessageCircle, Search } from 'lucide-react';
import api from './api';
import { getInitials, getSenderColor } from './useChat';
import * as S from './ChatPage.styled';

function formatPhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  let d = digits;
  if (d.startsWith('8')) d = '7' + d.slice(1);
  if (!d.startsWith('7') && d.length <= 10) d = '7' + d;
  const parts = ['+' + d[0]];
  if (d.length > 1) parts.push(' (' + d.slice(1, 4));
  if (d.length >= 4) parts[parts.length - 1] += ')';
  if (d.length > 4) parts.push(' ' + d.slice(4, 7));
  if (d.length > 7) parts.push('-' + d.slice(7, 9));
  if (d.length > 9) parts.push('-' + d.slice(9, 11));
  return parts.join('');
}

export default function ContactsPanel({ onStartChat, onClose }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/contacts');
      if (data.success) setContacts(data.contacts);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = contacts.filter(c => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.displayName || '').toLowerCase().includes(q)
      || (c.user?.username || '').toLowerCase().includes(q)
      || (c.phone || '').includes(q);
  });

  const handleStartChat = async (contact) => {
    if (!contact.user?.id) return;
    try {
      const { data } = await api.post('/chats', {
        type: 'direct',
        memberIds: [contact.user.id],
      });
      if (data.success) {
        onStartChat?.(data.chat.id);
        onClose?.();
      }
    } catch {}
  };

  const toggleFavorite = async (contact) => {
    try {
      const { data } = await api.patch(`/contacts/${contact.id}`, {
        isFavorite: !contact.isFavorite,
      });
      if (data.success) load();
    } catch {}
  };

  const deleteContact = async (contact) => {
    if (!window.confirm(`Удалить контакт "${contact.displayName}"?`)) return;
    try {
      await api.delete(`/contacts/${contact.id}`);
      load();
    } catch {}
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: 400, maxWidth: '90vw',
      background: 'white',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 90,
      display: 'flex', flexDirection: 'column',
      animation: 'slideIn 0.25s ease',
    }}>
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7B2FF7 0%, #E040A0 100%)',
        color: 'white',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
          <X size={18} />
        </button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 600 }}>Контакты</div>
        <button onClick={() => setShowAddModal(true)} style={{ background: 'rgba(255,255,255,0.25)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
          <UserPlus size={18} />
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e1e5e9' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 10, color: '#999' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Поиск контактов..."
            style={{ width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #e1e5e9', borderRadius: 20, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            {search ? 'Ничего не найдено' : (
              <>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📇</div>
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>Контактов пока нет</div>
                <div style={{ fontSize: 13 }}>Добавьте первый контакт по номеру телефона</div>
                <button
                  onClick={() => setShowAddModal(true)}
                  style={{ marginTop: 16, padding: '10px 20px', background: 'linear-gradient(135deg, #7B2FF7, #E040A0)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 500 }}
                >
                  + Добавить контакт
                </button>
              </>
            )}
          </div>
        ) : (
          filtered.map(c => (
            <div key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 16px', borderBottom: '1px solid #f1f3f5',
              transition: 'background 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: c.user?.avatar ? `url(${c.user.avatar}) center/cover` : `linear-gradient(135deg, ${getSenderColor(c.user?.id || c.id)}, ${getSenderColor(c.user?.id || c.id)}dd)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 14, fontWeight: 600, flexShrink: 0,
                position: 'relative',
              }}>
                {!c.user?.avatar && getInitials(c.displayName || c.user?.displayName || '?')}
                {c.user?.isOnline && (
                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: '#4CAF50', border: '2px solid white' }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 600, color: '#333', fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.displayName}
                  </span>
                  {c.isFavorite && <Star size={13} style={{ color: '#f5a623', fill: '#f5a623' }} />}
                </div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  {c.user?.username ? '@' + c.user.username : ''}
                  {c.phone ? ' · ' + c.phone : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button title="Написать" onClick={() => handleStartChat(c)} style={{ background: 'rgba(123, 47, 247, 0.1)', border: 'none', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7B2FF7' }}>
                  <MessageCircle size={16} />
                </button>
                <button title={c.isFavorite ? 'Убрать из избранного' : 'В избранное'} onClick={() => toggleFavorite(c)} style={{ background: 'transparent', border: 'none', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.isFavorite ? '#f5a623' : '#aaa' }}>
                  <Star size={16} fill={c.isFavorite ? '#f5a623' : 'none'} />
                </button>
                <button title="Удалить" onClick={() => deleteContact(c)} style={{ background: 'transparent', border: 'none', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddModal && <AddContactModal onClose={() => setShowAddModal(false)} onAdded={() => { setShowAddModal(false); load(); }} />}
    </div>
  );
}

function AddContactModal({ onClose, onAdded }) {
  const [phone, setPhone] = useState('');
  const [customName, setCustomName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notFoundPhone, setNotFoundPhone] = useState(null);

  const handleAdd = async () => {
    setError(''); setNotFoundPhone(null);
    if (!phone.trim()) { setError('Введите номер телефона'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/contacts', {
        phone: phone.replace(/\D/g, ''),
        customName: customName.trim() || undefined,
      });
      if (data.success) onAdded?.();
    } catch (err) {
      const d = err?.response?.data;
      if (d?.phoneNotFound) {
        setNotFoundPhone(phone);
        setError(d.message || 'Этот номер не зарегистрирован в tërka');
      } else {
        setError(d?.message || 'Не удалось добавить контакт');
      }
    } finally {
      setLoading(false);
    }
  };

  const inviteText = notFoundPhone
    ? `Привет! Установи tërka — защищённый мессенджер. Скачать: https://terka.bemulti.ru/app`
    : '';

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.Modal onClick={e => e.stopPropagation()}>
        <S.ModalHeader>
          Новый контакт
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}><X size={18} /></button>
        </S.ModalHeader>
        <S.ModalBody>
          <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 6 }}>Имя (как сохранить)</label>
          <S.ModalInput
            value={customName}
            onChange={e => setCustomName(e.target.value)}
            placeholder="Напр. Мама, Работа, Врач"
          />
          <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 6, marginTop: 8 }}>Телефон</label>
          <S.ModalInput
            type="tel"
            placeholder="+7 (___) ___-__-__"
            value={phone}
            onChange={e => setPhone(formatPhone(e.target.value))}
          />
          {error && (
            <div style={{ color: '#dc3545', fontSize: 13, marginTop: 8 }}>{error}</div>
          )}
          {notFoundPhone && (
            <div style={{ marginTop: 12, padding: 12, background: '#fff7e6', borderRadius: 8, fontSize: 13, color: '#8b6914' }}>
              Этого номера ещё нет в tërka. Пригласите человека:
              <textarea
                readOnly value={inviteText}
                style={{ width: '100%', marginTop: 8, padding: 8, border: '1px solid #e0c8a0', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', resize: 'none' }}
                rows={2}
                onClick={e => e.target.select()}
              />
              <button
                onClick={() => navigator.clipboard.writeText(inviteText)}
                style={{ marginTop: 6, padding: '6px 12px', background: '#f5a623', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
              >
                Скопировать приглашение
              </button>
            </div>
          )}
        </S.ModalBody>
        <S.ModalFooter>
          <S.ModalButton onClick={onClose}>Отмена</S.ModalButton>
          <S.ModalButton $primary disabled={loading || !phone.trim()} onClick={handleAdd}>
            {loading ? '...' : 'Добавить'}
          </S.ModalButton>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );
}
